import { BuildingSummary } from '../../types/protocols';

/**
 * Resolves a building type to its display label, injected by the caller.
 * Kept as a parameter (rather than importing `translate`) so this module stays
 * free of the app's Firebase-pulling import chain and remains unit-testable —
 * the same pattern used by buildingImageGrouping.
 */
export type TypeLabelResolver = (type: BuildingSummary['buildingType']) => string;

// ── Sort options ────────────────────────────────────────────────────

export type SortOption = 'name_asc' | 'building_type' | 'group_by_tag';

export interface SortOptionItem {
    key: SortOption;
    name: string;
}

// Order here is the order the options appear in the sort dropdown.
export const SORT_OPTIONS: SortOptionItem[] = [
    { key: 'name_asc', name: 'Bokstavsordning' },
    { key: 'building_type', name: 'Byggnadstyp' },
    { key: 'group_by_tag', name: 'Byggnadsgrupp' },
];

export const DEFAULT_SORT: SortOption = 'name_asc';

// Section key + label for the bucket holding buildings without any group tag.
export const UNGROUPED_SECTION_KEY = '__UNGROUPED__';
export const UNGROUPED_SECTION_LABEL = 'Utan grupp';

export interface BuildingTagSection {
    key: string;
    label: string;
    buildings: BuildingSummary[];
}

// ── Comparators ─────────────────────────────────────────────────────

function buildingName(building: BuildingSummary): string {
    return building.name?.toLowerCase() || '';
}

// Building name (A–Ö, Swedish collation) — the universal tiebreaker (R6).
function compareByName(a: BuildingSummary, b: BuildingSummary): number {
    return buildingName(a).localeCompare(buildingName(b), 'sv');
}

function compareByBuildingType(
    a: BuildingSummary,
    b: BuildingSummary,
    resolveTypeLabel: TypeLabelResolver,
): number {
    const labelA = resolveTypeLabel(a.buildingType).toLowerCase();
    const labelB = resolveTypeLabel(b.buildingType).toLowerCase();
    const byType = labelA.localeCompare(labelB, 'sv');
    return byType !== 0 ? byType : compareByName(a, b);
}

/**
 * Sort a flat list of buildings for the non-grouped sort modes. `group_by_tag`
 * has no flat ordering of its own, so it falls back to name ascending (used by
 * the map, which ignores order anyway). `resolveTypeLabel` is only consulted for
 * the `building_type` mode; it defaults to an empty label, leaving the name
 * tiebreaker in charge when no resolver is supplied.
 */
export function sortBuildingsFlat(
    buildings: BuildingSummary[],
    sort: SortOption,
    resolveTypeLabel: TypeLabelResolver = () => '',
): BuildingSummary[] {
    const copy = [...buildings];
    switch (sort) {
        case 'building_type':
            return copy.sort((a, b) => compareByBuildingType(a, b, resolveTypeLabel));
        case 'name_asc':
        case 'group_by_tag':
        default:
            return copy.sort(compareByName);
    }
}

// ── Grouping ────────────────────────────────────────────────────────

/** Sentinel key for the "Utan grupp" (untagged) option in group-tag filters.
 *  Shared by the map overview and the Förvaltning page so their persisted
 *  selections keep the same meaning. */
export const UNTAGGED_OPTION_VALUE = '__UNTAGGED__';

/** A building with no group tags — the shared definition used by both the
 *  group-tag filter and the "Utan grupp" section. */
export function isUntagged(building: BuildingSummary): boolean {
    return !building.groupTags || Object.keys(building.groupTags).length === 0;
}

/**
 * Build the tag sections for the "group by tag" sort mode.
 *
 * Sections are derived from each building's OWN `groupTags` entries (key + label
 * come straight off the building), NOT from the master tag list. This keeps the
 * grouped view robust to drift — tags missing from the master `buildingTags`
 * node, or legacy buildings, still surface — and is why a building never silently
 * disappears just because its tag isn't in the master list.
 *
 * - A building appears under every tag it carries, so multi-tag buildings appear
 *   in multiple sections (R10).
 * - `allowedTagKeys` restricts which tags become sections: pass `null` when no
 *   group-tag filter is active (show every tag the buildings carry), or a Set of
 *   selected tag keys to limit sections to the active filter (R14) — a multi-tag
 *   building then appears only under its selected tags.
 * - Sections are ordered alphabetically by tag label (R9); buildings within each
 *   are ordered by name A–Ö (R12). Empty sections cannot occur (a section exists
 *   only because a building created it).
 * - When `includeUntagged`, untagged buildings form a final "Utan grupp" section,
 *   rendered last (R11), omitted when none are untagged.
 */
export function groupBuildingsByTag(
    buildings: BuildingSummary[],
    allowedTagKeys: Set<string> | null,
    includeUntagged: boolean,
): BuildingTagSection[] {
    const byKey = new Map<string, { label: string; buildings: BuildingSummary[] }>();
    const untagged: BuildingSummary[] = [];

    for (const building of buildings) {
        // Key sections by the building's OWN groupTags keys (the same keys the
        // filter and `allowedTagKeys` use); take the label from the value, falling
        // back to the key if a legacy entry lacks one.
        const entries = building.groupTags ? Object.entries(building.groupTags) : [];
        if (entries.length === 0) {
            untagged.push(building);
            continue;
        }
        for (const [tagKey, tagValue] of entries) {
            if (allowedTagKeys && !allowedTagKeys.has(tagKey)) continue;
            const existing = byKey.get(tagKey);
            if (existing) {
                existing.buildings.push(building);
            } else {
                byKey.set(tagKey, { label: tagValue?.tag ?? tagKey, buildings: [building] });
            }
        }
    }

    const sections: BuildingTagSection[] = Array.from(byKey.entries())
        .map(([key, { label, buildings: members }]) => ({
            key,
            label,
            buildings: members.sort(compareByName),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'sv'));

    if (includeUntagged && untagged.length > 0) {
        sections.push({
            key: UNGROUPED_SECTION_KEY,
            label: UNGROUPED_SECTION_LABEL,
            buildings: untagged.sort(compareByName),
        });
    }

    return sections;
}

// Sentinel key for buildings with no `buildingType` set (translate() falls
// back to "Odefinierat" for undefined, so they still get a section).
const UNDEFINED_TYPE_SECTION_KEY = '__UNDEFINED_TYPE__';

/**
 * Build the tag sections for the "group by building type" sort mode.
 * One section per distinct building type, ordered alphabetically by label
 * (matching groupBuildingsByTag); buildings within each ordered by name A–Ö.
 */
export function groupBuildingsByType(
    buildings: BuildingSummary[],
    resolveTypeLabel: TypeLabelResolver,
): BuildingTagSection[] {
    const byType = new Map<string, { label: string; buildings: BuildingSummary[] }>();

    for (const building of buildings) {
        const key = building.buildingType ?? UNDEFINED_TYPE_SECTION_KEY;
        const existing = byType.get(key);
        if (existing) {
            existing.buildings.push(building);
        } else {
            byType.set(key, { label: resolveTypeLabel(building.buildingType), buildings: [building] });
        }
    }

    return Array.from(byType.entries())
        .map(([key, { label, buildings: members }]) => ({
            key,
            label,
            buildings: members.sort(compareByName),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'sv'));
}
